import { useProducts } from "@/hooks/useProducts";
import { CheckCircle } from "lucide-react";
import { CompactTierSelector } from "./CompactTierSelector";
import type { EstimateLineItem } from "@/hooks/useEstimateLineItems";
import { cn } from "@/lib/utils";

interface TierOption {
  tier: 'good' | 'better' | 'best';
  product_id: string;
  product?: {
    name: string;
    brand?: string;
  };
  priceDifference?: number;
}

interface ProductDetailProps {
  lineItem: EstimateLineItem;
  showImage: boolean;
  showTierSelector?: boolean;
  availableTiers?: TierOption[];
  currentTier?: 'good' | 'better' | 'best';
  onTierChange?: (tier: 'good' | 'better' | 'best', productId: string) => void;
}

export function ProductDetail({ 
  lineItem, 
  showImage,
  showTierSelector = false,
  availableTiers = [],
  currentTier = 'good',
  onTierChange
}: ProductDetailProps) {
  const { products } = useProducts();
  const product = products?.find(p => p.id === lineItem.product_id);
  
  return (
    <div className="flex gap-4 p-4 rounded-lg bg-card border page-break-inside-avoid">
      {showImage && product?.image_url && (
        <div className="w-48 h-48 flex-shrink-0">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover rounded-lg border"
          />
        </div>
      )}

      <div className={cn("flex-1", showTierSelector && "w-[90%]")}>
        <div className="flex items-start justify-between mb-2">
          <div>
            <h4 className="text-xl font-semibold text-foreground">{lineItem.description}</h4>
            {product?.brand && (
              <p className="text-sm text-muted-foreground">{product.brand}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Quantity</p>
            <p className="font-medium text-foreground">
              {lineItem.quantity} {lineItem.unit || product?.unit}
            </p>
          </div>
        </div>

        {product?.customer_description && (
          <p className="text-muted-foreground mb-3">
            {product.customer_description}
          </p>
        )}

        {product?.selling_points && Array.isArray(product.selling_points) && product.selling_points.length > 0 && (
          <ul className="space-y-1">
            {(product.selling_points as string[]).map((point, idx) => (
              <li key={idx} className="flex items-start text-sm">
                <CheckCircle className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-foreground">{point}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tier Selector Column - 10% width */}
      {showTierSelector && availableTiers.length > 1 && onTierChange && (
        <div className="w-[10%] flex-shrink-0 flex items-start justify-end no-print">
          <CompactTierSelector
            currentTier={currentTier}
            availableTiers={availableTiers}
            onTierChange={onTierChange}
          />
        </div>
      )}
    </div>
  );
}
